/**
 * @desc: 公用下拉选择器数据
 * @created: 2020/4/15
 * @author:yeungyifan
 * @version : v1.0
 */
import * as Public from '@/api/public'
import * as Models from '@/api/selects'
import * as Selects from '@/configs/Select/index'

/**
 * @desc 首字母转换大写
 * @param {*} str 字符串
 */
function titleCase(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * @desc 拼接数组/对象 统一键值对 key=value label=label
 * @param {*} res 传入后台返回出来的下拉数据
 * @param {*} _VALUE // 后台返回数据中值的名字
 * @param {*} _LABEL // 后台返回数据中标题的名字
 */
function filterData(res, _VALUE, _LABEL) {
  let filteredData = []

  // object 处理方法
  const objFunction = () => {
    if (res && res.data instanceof Array) {
      filteredData = res.data.map(item => ({
        value: item[_VALUE],
        label: item[_LABEL]
      }))
    }

    return [{ value: '', label: '全部' }, ...filteredData]
  }

  // array 处理方法
  const arrFunction = () => {
    if (res && res.length) {
      filteredData = res.map(item => ({
        value: item[_VALUE],
        label: item[_LABEL]
      }))
    }

    return [{ value: '', label: '全部' }, ...filteredData]
  }

  if (res instanceof Array) {
    return arrFunction()
  } else if (res instanceof Object) {
    return objFunction()
  } else {
    return []
  }
}

function filterChannelData(res, _VALUE, _LABEL, _BANKEXIT) {
  let filteredData = []

  // object 处理方法
  const objFunction = () => {
    if (res && res.data instanceof Array) {
      filteredData = res.data.map(item => ({
        value: item[_VALUE],
        label: item[_LABEL],
        bankExit: item[_BANKEXIT],
      }))
    }

    return [{ value: '', label: '全部', bankExit: '' }, ...filteredData]
  }

  // array 处理方法
  const arrFunction = () => {
    if (res && res.length) {
      filteredData = res.map(item => ({
        value: item[_VALUE],
        label: item[_LABEL],
        bankExit: item[_BANKEXIT],
      }))
    }

    return [{ value: '', label: '全部', bankExit: '' }, ...filteredData]
  }

  if (res instanceof Array) {
    return arrFunction()
  } else if (res instanceof Object) {
    return objFunction()
  } else {
    return []
  }
}

// 通过传入需要匹配的list和匹配条件value 返回list里对应value的label
function getLabelByValue(list, val) {
  for (const item of list) {
    // '' / null 不显示 ”全部“ label
    if (!item || !val) {
      return ''
    } else if (item.value == val) {
      return item.label
    }
  }

  return ''
}

export default {
  namespaced: true,
  state: {
    // 通过传入的value获取label方法
    getLabel: getLabelByValue,
    // 渠道
    channelList: [],
    // 收单机构
    companyList: [],
    // 城市
    areaList: [],
    // 城市激活下拉
    activeCityList: [],
    // 城市服务商
    cityServiceList: [],
    // 行业类型 - 供应商
    mccList: [],
    // 行业类型 - 商户
    mccBaseList: [],
    // 品类信息
    categoryList: [],
    // 调用方
    queryCallerVoList: [],

    // 企业类型
    typeList: Selects.typeList,
    // 用户状态下拉
    stateAuditList: Selects.stateAuditList,
    // 店铺性质
    supNatureList: Selects.supNatureList,
    // 是否
    isCode: Selects.isCode,
    // 关系
    relationshipList: Selects.relationshipList,
    // 业主性质
    ownerNatureList: Selects.ownerNatureList,
    // 审核状态下拉
    approvalStatusList: Selects.approvalStatusList,
    // 供应商信息是否有更新
    enterpriseIfUpdateList: Selects.enterpriseIfUpdateList,
    // 评级
    gradeList: Selects.gradeList,
    // 结算方式
    settleMethodList: Selects.settleMethodList,
    // 结算机构
    settleCompanyList: Selects.settleCompanyList,
    // 批复状态
    auditStatusList: Selects.auditStatusList,
    // 人工复核
    artificialAuditList: Selects.artificialAuditList,
    // 要素不通过
    elementfailList: Selects.elementfailList,
    // 外卖平台
    takeawayList: Selects.takeawayList,
    // 逾期阶段
    overdueStageList: Selects.overdueStageList,
    // 是否开通
    isOpenList: Selects.isOpenList,
    // 升/降额度
    applyTypeList: Selects.applyTypeList,
    // 额度调控 额度调整类型
    quotaTypeList: Selects.quotaTypeList,
    // 支付标识
    payTypeList: Selects.payTypeList,
    // 计费方式
    billingModeLisT: Selects.billingModeLisT,
    // 激活状态
    activeTypeList: Selects.activeTypeList,
    // 驳回申请 商户
    merOptions: Selects.merOptions,
    // 驳回申请 供应商
    supOptions: Selects.supOptions,
    // 线索转化 拒绝理由
    denialReason: Selects.denialReason,
    // 受理复核
    acceptanceReview: Selects.acceptanceReview,
  },

  mutations: {
    setSelectslList: (state, payload) => {
      state[payload.stateName] = payload.arr || payload.resultData
    }
  },

  actions: {
    // 渠道
    getChannelList({ commit, state: { channelList } }, payload, stateName = 'channelList') {
      if (channelList && channelList.length) {
        return channelList
      }

      return Public.getChannelList(payload).then(res => {
        const resultData = filterChannelData(res, 'id', 'bankName', 'bankExit')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 收单机构
    getCompanyList(
      { commit, state: { companyList } },
      payload,
      stateName = 'companyList'
    ) {
      if (companyList && companyList.length) {
        return companyList
      }

      return Public.getCompanyList(payload).then(res => {
        const resultData = filterData(res, 'id', 'name')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 可选通道接口
    getPaychannels(
      { commit, state: { paychannelsList } },
      payload,
      stateName = 'paychannelsList'
    ) {
      if (paychannelsList && paychannelsList.length) {
        return paychannelsList
      }

      return Public.getPaychannels(payload).then(res => {
        const resultData = filterData(res, 'id', 'passagewayName')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 扩展人员
    getListExpand(
      { commit, state: { expandList } },
      payload,
      stateName = 'expandList'
    ) {
      if (expandList && expandList.length) {
        return expandList
      }

      return Public.getListExpand(payload).then(res => {
        const resultData = filterData(res, 'id', 'name')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 城市服务商
    getServiceList(

      { commit, state: { cityServiceList } },
      payload,
      stateName = 'cityServiceList'
    ) {
      if (cityServiceList && cityServiceList.length) {
        return cityServiceList
      }

      return Public.getServiceList(payload).then(res => {
        const resultData = filterData(res, 'id', 'avtName')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 城市
    getAllCityList(
      { commit, state: { areaList } },
      payload,
      stateName = 'areaList'
    ) {
      if (areaList && areaList.length) {
        return areaList
      }

      return Public.getAllCityList(payload).then(res => {
        const resultData = filterData(res, 'areaCode', 'name')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 城市下拉
    getSelectActiveCity(
      { commit, state: { activeCityList } },
      payload,
      stateName = 'activeCityList'
    ) {
      if (activeCityList && activeCityList.length) {
        return activeCityList
      }

      return Public.getSelectActiveCity(payload).then(res => {
        const resultData = filterData(res, 'areaCode', 'name')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },


    // 行业类型 - 供应商
    getMccList({ commit, state: { mccList } }, payload, stateName = 'mccList') {
      if (mccList && mccList.length) {
        return mccList
      }

      return Models.getMcc(payload).then(res => {
        const resultData = filterData(res, 'mcc', 'mccName')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 行业类型 - 商户
    getMccBaseList({ commit, state: { mccBaseList } }, payload, stateName = 'mccBaseList') {
      if (mccBaseList && mccBaseList.length) {
        return mccBaseList
      }

      return Models.getBaseMcc(payload).then(res => {
        const resultData = filterData(res, 'mcc', 'mccName')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 品类信息
    getCategoryList(
      { commit, state: { categoryList } },
      payload,
      stateName = 'categoryList'
    ) {
      if (categoryList && categoryList.length) {
        return categoryList
      }

      return Models.getCategory(payload).then(res => {
        const resultData = filterData(res, 'id', 'name')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 调用方
    getQueryCallerVoList(
      { commit, state: { queryCallerVoList } },
      payload,
      stateName = 'queryCallerVoList'
    ) {
      if (queryCallerVoList && queryCallerVoList.length) {
        return queryCallerVoList
      }

      return Models.getQueryCallerVo(payload).then(res => {
        const resultData = filterData(res, 'callerCode', 'callerName')
        commit('setSelectslList', { resultData, stateName })
        return resultData
      })
    },

    // 支付方式
    getPayTypeList({ commit, state: { payTypeList }, payload, stateName = 'payTypeList' }) {
      if (payTypeList && payTypeList.length) {
        return payTypeList
      }

    },


  }
}
